/**
 * DataTableCard — the standardized shell for tabular list grids.
 *
 * The `<table>` sibling of `MasterListCard` (which wraps card-rows). Both
 * render the same two-footer chrome the invoices list established as the
 * approved pattern:
 *
 *   ┌─ bordered card ──────────────────────────────────┐
 *   │  <table> (header + rows — the caller's children)  │  ← horizontal scroll
 *   ├──────────────────────────────────────────────────┤
 *   │  Footer 1 — visible-set stats strip (label·value) │  ← `footer`
 *   ├──────────────────────────────────────────────────┤
 *   │  Footer 2 — "Showing X–Y of N" + prev/page/next   │  ← `pagination`
 *   └──────────────────────────────────────────────────┘
 *
 * The caller owns the `<table>` (columns, rows, sort) and the per-list stat
 * values; this shell owns the container + both footer strips so every listing
 * looks identical. Pair `pagination` with `<ListPagination>` and a cursor
 * stack (see the vendors list for the canonical wiring).
 *
 * @see invoices/list/invoice-table.tsx — the original inline two-footer table
 * @see list-card.tsx (MasterListCard) — the card-row sibling
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface DataTableStat {
  label: string;
  /** The emphasised value (rendered bold + mono). */
  value: React.ReactNode;
  /** Optional trailing text after the value, non-bold/muted (e.g. "· Acme Co"). */
  suffix?: React.ReactNode;
  /** `bad` tints the value with the danger ink (e.g. overdue totals). */
  accent?: "default" | "bad";
}

export interface DataTableCardProps {
  /** The `<table>` element — the shell adds the horizontal-scroll wrapper. */
  children: React.ReactNode;
  /**
   * Footer 1 — the visible-set stats strip. Label/value tuples rendered with
   * thin dividers between them (matching the invoices list). Omit or pass an
   * empty array to hide the strip.
   */
  footer?: DataTableStat[];
  /**
   * Footer 2 — the pagination strip, typically a `<ListPagination>`. Omit to
   * hide it (e.g. a list with no paging at all).
   */
  pagination?: React.ReactNode;
  /** Extra classes on the outer card. */
  className?: string;
}

/**
 * Header normalization — forces every `<thead>` inside the card to one look,
 * regardless of the per-listing `<thead>`/`<th>` classes (some pages used
 * `bg-bg` + `text-xs`, others bare `<thead>`, some had no bottom border).
 * These descendant-variant utilities out-specify the element's own classes, so
 * the header bg, bottom border, text size/transform/colour, weight and row
 * height are identical app-wide. Column alignment (`text-left`/`text-right`)
 * and horizontal padding stay per-column. The bottom border is placed on the
 * `<th>` cells (not the `<thead>`) so it renders in both `border-collapse`
 * and `separate` tables without doubling up with a first-row top border.
 */
const HEADER_NORMALIZE =
  "[&_thead]:bg-raised " +
  "[&_thead_th]:border-b [&_thead_th]:border-line " +
  "[&_thead_th]:py-3 [&_thead_th]:text-xs [&_thead_th]:font-medium [&_thead_th]:uppercase [&_thead_th]:tracking-wide [&_thead_th]:text-muted";

export function DataTableCard({ children, footer, pagination, className }: DataTableCardProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-line bg-paper shadow-sm",
        HEADER_NORMALIZE,
        className,
      )}
    >
      <div className="overflow-x-auto">{children}</div>

      {footer && footer.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          {footer.map((stat, i) => (
            <React.Fragment key={`${stat.label}-${i}`}>
              {i > 0 ? <span aria-hidden className="h-3 w-px bg-line-2" /> : null}
              <span className="inline-flex items-center gap-1.5">
                {stat.label}{" "}
                <strong
                  className={cn(
                    "font-mono tabular-nums font-bold",
                    stat.accent === "bad" ? "text-bad" : "text-ink",
                  )}
                >
                  {stat.value}
                </strong>
                {stat.suffix != null ? <span className="text-muted">{stat.suffix}</span> : null}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {pagination ? <div className="border-t border-line">{pagination}</div> : null}
    </div>
  );
}
